/*
 * Authors: mariushirjanu
 * Created: 29/06/2023
 */
import { HttpDataService, IHttpDataCache } from '../../../shared/http/http-data.service';

import { ICodeValue, ICompanyCountry, IContact, ICountry, ICurrency, ICurrency2, IDescribedObject, IDescribedStringObject, ILocation, ILocationExtended, INamedObject, IQuestion, IRecord, IStringRecord, IValueObject, IWebshop } from '../../../../type_definitions/datatypes';
import { IOrderType } from './order-info/order-entry-info-data.service';
import { ICartItemStatus, IPricesResponse } from '../cart/price-cart.service';
import { IExternalValue, IExternalValueContext, IInvoiceLocation } from './location/order-entry-location-data.service';
import { IAddressData, IContactModel } from './location/base-order-entry-location-to.service';
import { IDocumentGroup } from './documents/order-entry-documents-data.service';
import { Injectable } from '@angular/core';
import { POType } from '../../payment/payment-data.service';

export enum ProcurementType {
  BUY = 'BUY',
  LEASE = 'LEASE'
}

export interface IOrderPaymentType {
  billingType:'LOCAL' | 'CENTRAL';
  paymentTypes:string[];
}

export interface ITaxInfo {
  taxPercentage:number;
  taxExempt:boolean;
}

export interface IOrderContext {
  id?:number;
  punchoutProtocol:PunchoutProtocols;
  billingType:BillingType;
  centralPaymentTerms:number;
  localPaymentTerms:number;
  isTestOrder:boolean;
  invoiceFixedListSelection:boolean;
  shippingFixedListSelection:boolean;
  shippingAllowDefaultAddress:boolean;
  catalogLayout:string;
  billingTypeChangeAllowed:boolean;
  billingTypeDisplayAllowed:boolean;
  currencyChangeAllowed:boolean;
  creditCardPaymentsAllowed:boolean;
  paypalPaymentsAllowed:boolean;
  poPaymentsAllowed:boolean;
  poType:POType;
  allowOvernightShipment:boolean;
  paymentType:string;
  costCenterRequired:boolean;
  requesterAllowed:boolean;
  postUrl:string;
  postTarget:string;
  lamFeeDisplayAllowed:boolean;
  acslFeeDisplayAllowed:boolean;
  orderPermissions:IOrderPermissions;
  customerHasSubCompanies:boolean;
  customerSubCompanyMandatory:boolean;
  needOrderConfirmation:boolean;
  customerCurrencies:ICurrency[];
  tagField:IOrderContextTag;
  tagField2:IOrderContextTag;
  defaultOrderCurrency:ICurrency;
  countryManager:{
    phoneNumber:string;
    mobileNumber:string;
    name:string;
    id:number;
  };
  bp:{
    location:ILocation;
  };
  showStock:boolean;
  buyingGuide:{
    id:number;
    orderType:string; // not sent from server, but used on the client
  };
  hidePrices:boolean;
  orderApprovalPOMandatory:boolean;
  invoiceEmailAddress:string;
  approvalNeeded:boolean; // not sent from server, but used on the client
  bpInvoiceLocationExternalId?:IExternalValueContext;
  allowedPaymentTypes:IOrderPaymentType[];
  minimalClearingState:string;
  showProposeAlternatives:boolean;
  catalogSort:string;
  defaultTaxInfo:ITaxInfo;
  stockOrderType?:IDescribedStringObject;
  allowLease:boolean;
  allowMasterStockOrders:boolean;
  hideOrderEntryInvoiceTab?:boolean;
  showOnlyInStockItems?:boolean;
  defaultProcurementType?:ProcurementType;
}

interface IOrderLocation extends ILocationExtended {
  id:number;
  name:string;
  address:string;
  zipCode:string;
  city:string;
  telephoneNumber:string;
  faxNumber:string;
  homePage:string;
  vatNumber:string;
  companyCountry:ICompanyCountry;
}

export interface ILocationContact {
  addressType:string;
  office:string;
  contactType:string;
  comment:string;
  overnightShipment:boolean;
  location:IOrderLocation;
  contact:IContact;
  externalValue?:IExternalValue;
}

interface IOrderLineResponse {
  id:number;
  productId:number;
  title:string;
  shortDescription:string;
  quantity:number;
  part:string;
  partSuffix:string;
  partSuffixDescription:string;
  partConfigurationExist:boolean;
  globalPart:string;
  description:string;
  unitPrice:number;
  extUnitPrice:number;
  extCurrency:string;
  currency:string;
  orderLineStatus:string;
  orderLineStatusDescription:string;
  isIncludedInCart:boolean;
  toBeDeletedFromCart:boolean;
  category:string;
  vendorId:number;
  vendor:string;
  eolDate:number;
  categoryDetail:string;
  osLanguage:string;
  keyboard:string;
  alternativeForOrderLine:string;
  includedInOrderLine:number;
  comment:string;
  initialUnitPrice:number;
  catalogUnitPrice:number;
  eolDateCatalog:number;
  catalogId:number;
  sourceType:string;
  isMachine:boolean;
  compatibleProducts:boolean;
  taxPercentage:number;
}

export interface IRequesterAddress {
  type:string;
  description:string;
  vat:{
    name:string;
    regex:string;
    format:string
  };
  location:IOrderLocation;
}

export interface IOrderLeasePrices<T = IOrderPrices> {
  monthly?:T;
  total?:T;
}

export interface IOrderPrices {
  totalQuantity?:number;
  totalNumberOfParts?:number;
  subtotal:number;
  chargesTotal:number;
  total:number;
  tax:number;
}

export interface IOrderResponse {
  id:number;
  webshop:IWebshop;
  orderDescription:string;
  stockOrderType:IDescribedStringObject;
  orderType:IOrderType;
  country:ICountry;
  workflowType:string;
  isTestOrder:boolean;
  mainStatus:string;
  orderStatusDescription:string;
  billingType:BillingType;
  taxPercentage:number;
  orderCurrency:ICurrency2;
  payment:{
    paymentType:IDescribedStringObject;
    redirectRequired:boolean;
  };
  bp:{
    location:IOrderLocation;
  };
  costCenter:IDescribedObject;
  tagValue:string;
  tagValue2:string;
  requester:{
    id:number;
    fullName:string;
    firstname:string;
    lastname:string;
    email:string;
    directPhone:string;
    address:IRequesterAddress;
  };
  shipto:ILocationContact;
  invoiceto:ILocationContact & IInvoiceLocation;
  customerReference:string;
  customerPONumber:string;
  requestedArrivalDate:number;
  comment:string;
  items:IOrderLineResponse[];
  purchasePrices:IOrderPrices;
  leasePrices:IOrderLeasePrices;
  orderPermissions:IOrderPermissions;
  invoiceby:{
    address:string;
    zipCode:string;
    city:string;
    country:string;
    company:string;
    vatNumber:string;
    contact:string;
  };
  charges:IOrderCharge[];
  context:IOrderContext;
  orderCreationDate:Date;
  proposeAlternatives:string;
}

export interface IOrderLineUpdate {
  id:number;
  productId:number;
  partSuffix:string;
  partSuffixDescription:string;
  quantity:number;
  extUnitPrice:number;
  extCurrency:string;
  description:string;
  category:string;
  catalogId:number;
  vendorId:number;
  part:string;
  sourceType:string;
  title:string;
  shortDescription:string;
}

export interface IOrderUpdate {
  action:string;
  noMailNotifications:boolean;
  isTestOrder:boolean;
  bp:IRecord;
  requester:IRecord;
  orderCurrency:{
    isoCode:string;
  };
  billingType:string;
  shipto:{
    addressType:string;
    defaultAddress:boolean;
    location:{
      id:number;
      address:string;
      zipCode:string;
      city:string;
      vatNumber:string;
      country:IRecord;
      state:IStringRecord;
      company:{
        name:string;
      };
    };
    office:string;
    overnightShipment:boolean;
    comment:string;
    contact:{
      fullName:string;
      email:string;
      directPhone:string;
    };
    contactType:string;
  };
  invoiceto:{
    addressType:string;
    defaultAddress:boolean;
    location:{
      id:number;
      address:string;
      zipCode:string;
      city:string;
      vatNumber:string;
      country:IRecord;
      state:IStringRecord;
      company:{
        name:string;
      };
    };
    office:string;
    comment:string;
    contact:{
      fullName:string;
      email:string;
      directPhone:string;
    };
    contactType:string;
  };
  orderDescription:string;
  customerReference:string;
  customerPONumber:string;
  requestedArrivalDate:number;
  orderType:IRecord;
  costCenter:IRecord;
  stockOrderType:IStringRecord;
  comment:string;
  tagValue:string;
  tagValue2:string;
  payment:{
    paymentType:IStringRecord;
    interactivePayment:boolean;
  };
  items:IOrderLineUpdate;
  acceptExtraCharges:boolean;
  acceptAllTAndC:boolean;
  addDocuments:{
    id:number;
    caveatDefinitionId:number
  }[];
  deleteDocuments:number[];
  taxPercentage:number;
  charges:IOrderCharge[];
  approvalReason:string;
  proposeAlternatives:string;
}

export interface IOrderPermissions {
  editable:boolean;
  allowApplyVAT:boolean;
  allowRecalculateCharges:boolean;
  allowCancelOrder:boolean;
  allowSubmit:boolean;
  allowApprovalReminder:boolean;
  allowSaveForLater:boolean;
  allowToApprove:boolean;
  editableOwnerTypes:string[];
}

export interface IOrderContextTag {
  label:string;
  description:string;
  pattern:string;
  required:boolean;
  regex?:string;
}

export interface IOrderInfoPayment {
  paymentType:{
    id:string;
    description:string;
  };
  customerPONumber:string;
}

export interface IOrderInfo {
  orderCreationDate:Date;
  description:string;
  referenceCustomer:string;
  customerPONumber:string;
  tagValue:string;
  tagValue2:string;
  arrivalDate:Date;
  orderType:IOrderType;
  costCenter:IDescribedObject;
  stockOrderType: IValueObject;
  comment:string;
  prices?:IPricesResponse;
  payment?:IDescribedStringObject;
}

export interface IOrderCancelOption extends IDescribedObject {
  stepCode:number;
}

export interface IOrderWarning {
  warnings:string;
}

export interface IOrderThankYou {
  thankYou:string;
  documents:IDocumentGroup[];
  warnings:string;
}

export interface IOrderCharge {
  orderNumber?:number;
  chargeId:number;
  chargeType:IDescribedStringObject;
  ownerType:ICodeValue;
  value:number;
  description:string;
  marginId?:number;
  standardContractLineId?:number;
  manual:boolean;
  editable?:boolean;
}

export interface IPONumberCheck {
  orderId:number;
  webshopName:string;
  orderDate:string;
  countryName:string;
}

export type BillingType = 'LOCAL' | 'CENTRAL';

export interface IOrderEntryOverviewModel {
  cart:{
    cartStatus:ICartItemStatus[];
    hasWarnings:() => boolean;
  };
  country:{
    selectedShop:INamedObject;
    selectedCountry:ICountry;
    selectedBP:INamedObject;
  };
  documents:IDocumentGroup[];
  fees:{
    lamFee:string;
    acslFee:string;
  };
  requester:{
    contact:IContact;
    address:IRequesterAddress;
  };
  orderInfo:IOrderInfo;
  payment:IOrderInfoPayment;
  shippedTo:{
    address:IAddressData;
    contact:IContactModel;
    comments:string;
    overnight:boolean;
    office?:string;
  };
  shippedFrom:{
    bp:ILocationExtended;
    countryInfoQuestions:IQuestion[];
  };
  invoiceTo:{
    address:IAddressData;
    contact:IContactModel;
    comments:string;
    billingType:string;
    eInvoiceMailAddress:string;
    office?:string;
  };
  invoiceBy:{
    address:ILocationExtended;
    contact:IContact;
  };
}

export enum PunchoutProtocols {
  CXML = 'CXML',
  OCI = 'OCI'
}

@Injectable()
export class OrderEntryDataService {
  private cache:IHttpDataCache<any>;

  constructor(
    private httpDataService:HttpDataService
  ) {
    this.cache = httpDataService.createCache();
  }

  public order(orderId:number):Promise<IOrderResponse> {
    return this.cache.doGET(
      `rest/orderentry/orders/${orderId}`,
      undefined,
      undefined,
      'Error loading order'
    );
  }

  public createOrder(webshopId:number, countryId:number, data:IOrderUpdate):Promise<IOrderResponse> {
    return this.httpDataService.doPOST(
      `rest/orderentry/webshops/${webshopId}/countries/${countryId}/orders`,
      undefined,
      data,
      undefined,
      'Error creating order'
    );
  }

  public saveOrder(orderId:number, data:IOrderUpdate):Promise<IOrderResponse> {
    return this.httpDataService.doPATCH(
      `rest/orderentry/orders/${orderId}`,
      undefined,
      data,
      undefined,
      'Error saving order'
    );
  }

  public cancelOrder(orderId:number, stepId:number, comment:string, noMailNotifications:boolean):Promise<string> {
    return this.httpDataService.doPUT(
      `rest/orderentry/orders/${orderId}/cancel`,
      {
        stepId,
        comment,
        noMailNotifications
      },
      undefined,
      undefined,
      'Error canceling order'
    );
  }

  public warnings(webshopId:number, countryId:number, data, orderId:number):Promise<IOrderWarning> {
    return this.httpDataService.doPOST(
      `rest/orderentry/webshops/${webshopId}/countries/${countryId}/warnings`,
      { orderId },
      data,
      undefined,
      'Error loading warnings'
    );
  }

  public thankyou(webshopId:number, countryId:number, companyCountryId:number, orderId:number):Promise<IOrderThankYou> {
    return this.httpDataService.doGET(
      `rest/orderentry/webshops/${webshopId}/countries/${countryId}/thankyou`,
      {
        companyCountryId,
        orderId
      },
      undefined,
      'Error loading thankyou'
    );
  }

  public itemsAction(orderId:number, action:string):Promise<void> {
    return this.httpDataService.doPATCH(
      `rest/orderentry/orders/${orderId}/items`,
      undefined,
      { action: 'CLEARWARNINGS' },
      undefined,
      `Error items action: ${action}`
    );
  }

  public cancelOptions(orderId:number):Promise<IOrderCancelOption[]> {
    return this.httpDataService.doGET(
      `rest/orderentry/orders/${orderId}/canceloptions`,
      undefined,
      undefined,
      'Error getting cancel options'
    );
  }

  public loadPermissions(orderId?:number):Promise<void> {
    return this.httpDataService.doGET(
      `rest/orderentry/orderpermissions`,
      { orderId },
      undefined,
      'Error getting order permissions'
    );
  }

  public sendReminder(orderId:number):Promise<void> {
    return this.httpDataService.doGET(
      `rest/orderentry/orders/${orderId}/remind`,
      undefined,
      `Sending reminder for order ${orderId}`,
      'Error sending reminder'
    );
  }

  public checkCustomerPONumber(poNumber:string, webshopId:number, paymentType:string):Promise<IPONumberCheck> {
    return this.httpDataService.doGET(
      'rest/orderentry/customerponumber',
      {
        poNumber,
        webshopId,
        paymentType
      },
      undefined,
      'Error checking PO number'
    );
  }

  public clear():void {
    this.cache.clear();
  }
}